// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { Radio, RadioGroup } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import { ChangeEvent } from 'react';
import { useTranslate } from '@tolgee/react';
import { AddressAutofill } from '@mapbox/search-js-react';
import PhoneInput from 'react-phone-number-input/min';
import { CountryCode } from 'libphonenumber-js';
import Card from '@app/components/card/card.tsx';
import CardHeader from '@app/components/card/card-header.tsx';
import CardTableContent from '@app/components/card/card-content-table.tsx';
import CardContent from '@app/components/card/card-content.tsx';
import ListItem from '@app/components/list/list-item.tsx';
import TextInput from '@app/components/form/input/text-input.tsx';
import FormItem from '@app/components/form/form-item.tsx';
import FormLayout from '@app/components/form/form-layout.tsx';
import DualItem from '@app/components/form/dual-form-item.tsx';
import { BranchItemFragment, OnlineOrderDeliveryMode } from '@app/graphql/types/graphql.ts';
import { COUNTRY_LOCALE_MAP } from './autofill-address-input.tsx';
import '../phone-style.css';

export interface CardSelectionData<T> {
  id: string;
  value: T;
  title: string;
  description: string;
  additionalText: string[];
}

interface UnifiedDeliveryContactCardProps {
  // Delivery mode props
  deliveryModes: CardSelectionData<OnlineOrderDeliveryMode>[];
  selectedDeliveryMode: CardSelectionData<OnlineOrderDeliveryMode>;
  onDeliveryModeChange: (value: CardSelectionData<OnlineOrderDeliveryMode>) => void;
  disableDeliveryMode: boolean;

  // Form values
  values: {
    streetAddress: string;
    streetAddress2: string;
    addressLocality: string;
    postalCode: string;
    guestName: string;
    email: string;
    phone: string;
    message: string;
  };

  // Form handlers
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  manualHandle: (name: string, value: unknown) => void;

  // Errors
  errors?: Record<string, string>;

  // Branch data for country configuration
  branch: BranchItemFragment;
}

export default function UnifiedDeliveryContactCard(props: UnifiedDeliveryContactCardProps) {
  const { t } = useTranslate();
  const accessToken = import.meta.env.VITE_MAP_BOX as string;
  const country = COUNTRY_LOCALE_MAP[props.branch.addressCountry] ?? { code: 'DE', language: 'de' };

  return (
    <Card className="card unified-delivery-contact-card">
      <CardHeader
        title={t(
          'online-order.fill-contact-page.form-input.unified-card.title',
          'Delivery & Contact Information'
        )}
      />

      {/* Delivery Mode Selection */}
      <CardTableContent>
        <RadioGroup value={props.selectedDeliveryMode} onChange={props.onDeliveryModeChange}>
          {props.deliveryModes.map((item) => {
            const isDisabledItem =
              props.disableDeliveryMode && item.id.toString() === OnlineOrderDeliveryMode.Delivery.toString();
            const isDelivery = item.value === OnlineOrderDeliveryMode.Delivery;
            const isSelected = props.selectedDeliveryMode.id === item.id;

            return (
              <div key={item.id}>
                <Radio value={item} aria-label={item.title} aria-description={item.description} disabled={isDisabledItem}>
                  {({ checked }) => (
                    <ListItem
                      sideContent={item.additionalText.map((text, index) => (
                        <span key={index} className="hidden text-sm font-medium text-gray-900 sm:block">
                          {text}
                        </span>
                      ))}
                      sideContentAlign={'center'}
                      className={classNames('flex flex-1 hover:bg-gray-50', isDisabledItem && 'opacity-50')}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center">
                          <CheckCircleIcon
                            className={classNames(!checked || isDisabledItem ? 'hidden' : '', 'h-5 w-5 text-gray-700')}
                            aria-hidden="true"
                          />

                          <div
                            className={classNames(
                              !checked || isDisabledItem ? '' : 'hidden',
                              'h-4 w-4 rounded-full border border-2 border-gray-800 hover:border-gray-400'
                            )}
                          ></div>
                        </div>

                        <div className="flex flex-col">
                          <p className="block text-sm font-medium text-gray-900">{item.title}</p>
                          <p className="flex max-w-72 items-center text-sm text-gray-500">{item.description}</p>

                          <div className="mt-4 flex flex-col sm:hidden">
                            {item.additionalText.map((text, index) => (
                              <span key={index} className="text-sm font-medium text-gray-900">
                                {text}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </ListItem>
                  )}
                </Radio>

                {/* Conditional Address Fields - shown when Delivery is selected */}
                {isDelivery && isSelected && (
                  <div className="px-4 pb-4">
                    <AddressAutofill
                      accessToken={accessToken}
                      options={{
                        country: country.code,
                        language: country.language,
                      }}
                    >
                      <FormLayout className="checkout-form__address-layout pt-4">
                        <FormItem
                          title={t('online-order.fill-contact-page.form-input.delivery.address-1.label', 'Address line 1')}
                          className="checkout-form__field checkout-form__field--address-line-1"
                        >
                          <TextInput
                            label="streetAddress"
                            type="text"
                            name="streetAddress"
                            autoComplete="address-line1"
                            className="checkout-form__input checkout-form__input--address-line-1 mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            placeholder={t(
                              'online-order.fill-contact-page.form-input.delivery.address-1.placeholder',
                              'Address line 1'
                            )}
                            value={props.values.streetAddress}
                            error={props.errors?.streetAddress}
                            onChange={(e) => {
                              props.manualHandle('streetAddress', e.target.value);
                            }}
                          />
                        </FormItem>

                        <FormItem
                          title={t('online-order.fill-contact-page.form-input.delivery.address-2.label', 'Address line 2')}
                          className="checkout-form__field checkout-form__field--address-line-2"
                        >
                          <TextInput
                            label="streetAddress2"
                            type="text"
                            name="streetAddress2"
                            autoComplete="address-line2"
                            className="checkout-form__input checkout-form__input--address-line-2 mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            placeholder={t(
                              'online-order.fill-contact-page.form-input.delivery.address-2.placeholder',
                              'Address line 2'
                            )}
                            value={props.values.streetAddress2}
                            error={props.errors?.streetAddress2}
                            onChange={props.onChange}
                          />
                        </FormItem>

                        <DualItem className="checkout-form__dual-field">
                          <FormItem
                            title={t('online-order.fill-contact-page.form-input.delivery.city.label', 'City')}
                            className="checkout-form__field checkout-form__field--city"
                          >
                            <TextInput
                              type="text"
                              name="addressLocality"
                              autoComplete="address-level2"
                              className="checkout-form__input checkout-form__input--city mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              placeholder={t('online-order.fill-contact-page.form-input.delivery.city.placeholder', 'City')}
                              value={props.values.addressLocality}
                              error={props.errors?.addressLocality}
                              onChange={props.onChange}
                            />
                          </FormItem>

                          <FormItem
                            title={t('online-order.fill-contact-page.form-input.delivery.postal-code.label', 'Postal code')}
                            className="checkout-form__field checkout-form__field--postal-code"
                          >
                            <TextInput
                              type="text"
                              name="postalCode"
                              autoComplete="postal-code"
                              className="checkout-form__input checkout-form__input--postal-code mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              placeholder={t('online-order.fill-contact-page.form-input.delivery.postal-code.placeholder', 'Zip')}
                              value={props.values.postalCode}
                              error={props.errors?.postalCode}
                              onChange={props.onChange}
                            />
                          </FormItem>
                        </DualItem>
                      </FormLayout>
                    </AddressAutofill>
                  </div>
                )}
              </div>
            );
          })}
        </RadioGroup>
      </CardTableContent>

      {/* Contact Information Section - Always Visible */}
      <CardContent className="contact-form__content">
        <FormLayout className="contact-form__fields">
          <FormItem
            title={t('online-order.fill-contact-page.form-input.delivery.contact-data.name.label', 'Your name')}
            className="mp-2 max-w-md"
          >
            <TextInput
              type="text"
              label="guestName"
              value={props.values.guestName}
              name="guestName"
              placeholder={t(
                'online-order.fill-contact-page.form-input.delivery.contact-data.name.placeholder',
                'Name'
              )}
              onChange={(e) => {
                props.onChange(e as ChangeEvent<HTMLInputElement>);
              }}
              error={props.errors?.guestName}
            />
          </FormItem>

          <DualItem>
            <FormItem
              title={t(
                'online-order.fill-contact-page.form-input.delivery.contact-data.email.label',
                'Your email address'
              )}
              className="max-w-md"
            >
              <TextInput
                type="text"
                label="email"
                value={props.values.email}
                name="email"
                placeholder={t(
                  'online-order.fill-contact-page.form-input.delivery.contact-data.email.placeholder',
                  'Email'
                )}
                onChange={(e) => {
                  props.onChange(e as ChangeEvent<HTMLInputElement>);
                }}
                error={props.errors?.email}
              />
            </FormItem>

            <FormItem
              title={t(
                'online-order.fill-contact-page.form-input.delivery.contact-data.phone.label',
                'Your phone number'
              )}
              className="max-w-md"
            >
              <PhoneInput
                className="w-full overflow-hidden rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                defaultCountry={country.code as CountryCode}
                placeholder={t(
                  'online-order.fill-contact-page.form-input.delivery.contact-data.phone.placeholder',
                  'Enter phone number'
                )}
                name="phone"
                value={props.values.phone}
                onChange={(value) => {
                  props.manualHandle('phone', value ? value.toString() : '');
                }}
              />
              {props.errors?.phone && (
                <p className="mt-2 text-sm text-red-600" id="phone-error">
                  {props.errors.phone}
                </p>
              )}
            </FormItem>
          </DualItem>

          <FormItem
            title={t('online-order.fill-contact-page.form-input.delivery.contact-data.message.label', 'Message')}
          >
            <TextInput
              name="message"
              label="message"
              onChange={(newVal) => {
                props.manualHandle('message', newVal.target.value);
              }}
              placeholder={t(
                'online-order.fill-contact-page.form-input.delivery.contact-data.message.placeholder',
                'Your additional message to restaurant'
              )}
              value={props.values.message}
              type={'textarea'}
              error={props.errors?.message}
            />
          </FormItem>
        </FormLayout>
      </CardContent>
    </Card>
  );
}
